import type { ItemQuoteInput, TransactionIdentifier } from "@opensea/graphql"
import { graphql, useQuery } from "@opensea/graphql"
import { Flex } from "@opensea/ui-kit"
import { omit } from "es-toolkit/object"
import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { Button } from "../../../../../components/Button"
import type { WalletDestination } from "../../../../../components/DestinationWalletSelect/types"
import { TransactionFullScreen } from "../../../../../components/TransactionFullScreen"
import { TransactionReceipt } from "../../../../../components/TransactionReceipt"
import { TransactionReceiptFragment } from "../../../../../components/TransactionReceipt/TransactionReceipt.fragment"
import { ViewItemsOnProfileButton } from "../../../../../components/ViewItemsOnProfileButton"
import { FlowState } from "../../../../../stores/flowStore"
import type { CheckoutFlowState } from "../../../../flow-state"
import {
  useSetReceiptStatus,
  useSetReceiptStatusFragment,
} from "../../../../hooks/useSetReceiptStatus"

const FETCH_INTERVAL = 500

const query = graphql(
  `
    query CheckoutFlowStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
      $swapProvider: SwapProviderType!
    ) {
      buyReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
        swapProvider: $swapProvider
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
          }
        }
        failedItemReceipts {
          __typename
          item {
            id
          }
        }
        ...useSetReceiptStatus
        ...TransactionReceipt
      }
    }
  `,
  [TransactionReceiptFragment, useSetReceiptStatusFragment],
)

type CheckoutStatusProps = {
  transactionIdentifiers: TransactionIdentifier[]
  itemQuotes: ItemQuoteInput[]
  itemImageMap: Record<string, string | null>
  crossChain: boolean | undefined
  walletDestination?: WalletDestination
}

export const CheckoutStatus = ({
  transactionIdentifiers,
  itemQuotes,
  itemImageMap,
  crossChain,
  walletDestination,
}: CheckoutStatusProps) => {
  const t = useTranslations("wallet.CheckoutStatus")
  const { flow } = useSnapshot(FlowState)
  const isClaim = (flow as CheckoutFlowState)?.isClaim ?? false
  const [result, reexecuteQuery] = useQuery({
    query,
    variables: {
      transactionIdentifiers,
      itemQuotes,
      swapProvider: crossChain ? "RELAY" : "LOCAL_BUY_NFT",
    },
    pause: true,
  })

  useEffect(
    function pollForReceipt() {
      if (
        result.fetching ||
        (result.data?.buyReceipt &&
          result.data.buyReceipt.status !== "PENDING") ||
        transactionIdentifiers.length === 0
      ) {
        return
      }

      const timerId = setTimeout(() => {
        reexecuteQuery({ requestPolicy: "network-only" })
      }, FETCH_INTERVAL)

      return () => clearTimeout(timerId)
    },
    [
      result.fetching,
      reexecuteQuery,
      result.data?.buyReceipt,
      transactionIdentifiers.length,
      crossChain,
    ],
  )

  const receipt = result.data?.buyReceipt

  useSetReceiptStatus(receipt)

  const [receiptOpen, setReceiptOpen] = useState(false)

  if (!receipt || receipt.status === "PENDING") {
    const pendingImages = Object.values(itemImageMap)
    return (
      <TransactionFullScreen
        body={t(isClaim ? "pending.claimDescription" : "pending.description")}
        images={pendingImages}
        loading
        title={t(isClaim ? "pending.claimTitle" : "pending.title")}
      />
    )
  }

  if (receipt.status === "FAILED") {
    return (
      <TransactionFullScreen
        body={t(isClaim ? "failed.claimDescription" : "failed.description")}
        bottom={
          receiptOpen ? (
            <TransactionReceipt
              flowType="purchase"
              receipt={receipt}
              walletDestination={walletDestination}
            />
          ) : (
            <Button onClick={() => setReceiptOpen(true)} variant="secondary">
              {t("viewReceipt")}
            </Button>
          )
        }
        images={receipt.failedItemReceipts.map(item =>
          item.item ? itemImageMap[item.item.id] : null,
        )}
        monochrome
        title={t(isClaim ? "failed.claimTitle" : "failed.title")}
      />
    )
  }

  if (receipt.status === "PARTIAL_SUCCESS") {
    return (
      <TransactionFullScreen
        body={t("partialSuccess.description", {
          itemsPurchasedCount: receipt.itemReceipts.length,
          itemsFailedCount: receipt.failedItemReceipts.length,
        })}
        bottom={
          receiptOpen ? (
            <TransactionReceipt
              flowType="purchase"
              receipt={receipt}
              walletDestination={walletDestination}
            />
          ) : (
            <Flex className="gap-4">
              <Button onClick={() => setReceiptOpen(true)} variant="secondary">
                {t("viewReceipt")}
              </Button>
              <ViewItemsOnProfileButton
                itemIds={receipt.itemReceipts.flatMap(itemReceipt =>
                  itemReceipt.item ? [itemReceipt.item.id] : [],
                )}
                walletDestinationAddress={walletDestination?.address}
              />
            </Flex>
          )
        }
        images={receipt.itemReceipts.map(item =>
          item.item ? itemImageMap[item.item.id] : null,
        )}
        title={t("partialSuccess.title")}
      />
    )
  }

  return (
    <TransactionFullScreen
      bottom={
        receiptOpen ? (
          <TransactionReceipt
            flowType="purchase"
            receipt={receipt}
            walletDestination={walletDestination}
          />
        ) : (
          <Flex className="gap-4">
            <Button onClick={() => setReceiptOpen(true)} variant="secondary">
              {t("viewReceipt")}
            </Button>
            <ViewItemsOnProfileButton
              itemIds={receipt.itemReceipts.flatMap(itemReceipt =>
                itemReceipt.item ? [itemReceipt.item.id] : [],
              )}
              walletDestinationAddress={walletDestination?.address}
            />
          </Flex>
        )
      }
      // Add back when we add social links
      // body={t("success.description")}
      confetti
      images={receipt.itemReceipts.map(item =>
        item.item ? itemImageMap[item.item.id] : null,
      )}
      title={t(isClaim ? "success.claimTitle" : "success.title")}
    />
  )
}

export const CheckoutFlowStatus = ({
  walletDestination,
}: {
  walletDestination?: WalletDestination
}) => {
  const { receiptTransactions, itemQuotes, itemImageMap, flow } =
    useSnapshot(FlowState)

  const itemQuoteInputs = useMemo(
    () => itemQuotes.map(itemQuote => omit(itemQuote, ["orderId"])),
    [itemQuotes],
  )

  if (flow?.type !== "checkout") {
    throw new Error("Not supported flow type")
  }

  return (
    <CheckoutStatus
      crossChain={flow.crossChain}
      itemImageMap={itemImageMap}
      itemQuotes={itemQuoteInputs}
      transactionIdentifiers={receiptTransactions.map(txn => ({
        chain: txn.chain,
        transactionHash: txn.hash,
      }))}
      walletDestination={walletDestination}
    />
  )
}
